import moment from 'moment/moment';
import { DEFAULT_DATETIME_TIMEZONE_FORMAT } from '../../../constants/main';
import { FILE_MIME_TYPES, TRANSLATION_EXPORT_ALLOWED_NODE_TYPES, TRANSLATION_FILE_TYPES, TRANSLATION_REFERENCES_COLUMN, UTF_8_CHARSET } from '../../../constants/translations';
import * as PapaParse from 'papaparse';
import handleFileDownload from '../handleFileDownload';
import { t } from 'i18next';
import { po } from 'gettext-parser';
import openConsoleAlertMessage from '../openConsoleAlertMessage';

// Define a constant for the default options
const DEFAULT_EXPORT_OPTIONS = {
  exportData: {},
  primaryExportLanguage: 'en',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  fileName: 'placeholderFile',
  exportLanguages: []
};

export const onExportTranslations = ({
  fileType,
  options = {}
}) => {
  try {

    const mergedOptions = { ...DEFAULT_EXPORT_OPTIONS, ...options };

    const exportStrategy = exportTranslationContentStrategies[fileType];
    if (exportStrategy) {
      exportStrategy(mergedOptions);
    } else {
      throw new Error ('There was an error with exporting your content');
    }
  } catch(e) {
    console.error(e);
  }
};

/**
 * Strategy repository containing the strategy for each exported file type for content translations.
 * Each strategy function calls the generic exportFile function with the appropriate parameters.
 * @property {Function} csv - Executes the strategy for exporting CSV files.
 * @property {Function} pot - Executes the strategy for exporting POT files.
 */
const exportTranslationContentStrategies = {
  [TRANSLATION_FILE_TYPES.CSV.key]: (params) => exportFile(params, convertDataToCsv, FILE_MIME_TYPES[TRANSLATION_FILE_TYPES.CSV.key], TRANSLATION_FILE_TYPES.CSV.extension),
  [TRANSLATION_FILE_TYPES.POT.key]: (params) => exportFile(params, convertDataToPo, FILE_MIME_TYPES[TRANSLATION_FILE_TYPES.POT.key], TRANSLATION_FILE_TYPES.POT.extension)
};

/**
 * Executes the export process for a given file type by converting the data and initiating the file download.
 * @param {Object} params - The parameters required for exporting.
 * @param {Object} params.exportData - The data to be exported.
 * @param {String} params.primaryExportLanguage - The primary language code for the translations.
 * @param {String} [params.timezone='UTC'] - The timezone for file creation.
 * @param {String} params.fileName - The name of the file to be exported.
 * @param {Array} params.exportLanguages - The list of languages to be included in the export.
 * @param {Function} conversionStrategy - The strategy function to convert data into the desired format.
 * @param {String} mimeType - The MIME type of the file.
 * @param {String} fileExtension - The file extension for the exported file.
 */
const exportFile = ({ exportData, primaryExportLanguage, timezone, fileName, exportLanguages }, conversionStrategy, mimeType, fileExtension) => {
  try {
    const convertedData = conversionStrategy({
      data: exportData,
      primaryLanguage: primaryExportLanguage,
      timezone: timezone,
      exportLanguages: exportLanguages
    });
    const blob = new Blob([convertedData], { type: mimeType });
    handleFileDownload(blob, `${fileName}${fileExtension}`);
  } catch (e) {
    console.error(e);
    openConsoleAlertMessage({ type: 'error', message: t('There was an error exporting your file, please try again.') });
  }
};

/**
 * Converts provided data to CSV format for translation export.
 * @param {Object} data - The source data to be converted.
 * @param {String} primaryLanguage - The primary language code for the translations.
 * @param {String} _ - Unused parameter for timezone to match the signature of other conversion functions.
 * @param {Array} exportLanguages - The list of languages to be included in the export.
 * @returns {String} The CSV formatted data as a string.
 */
const convertDataToCsv = ({ data, primaryLanguage, exportLanguages }) => {
  let contentToBeExported = [];

  Object.entries(data).forEach(([key, value]) => {
    const translationsArray = value.flatMap(entity =>
      entity?.content?.nodes?.filter(node =>
        node?.label && Object.values(TRANSLATION_EXPORT_ALLOWED_NODE_TYPES).includes(node?.type)
      ).map(node => {
        const entityId = entity?.id;
        const nodeId = node?.id;
        const keyValue = `${key}:${entityId}/${nodeId}`;

        const translationObject = {
          [primaryLanguage]: node?.label[primaryLanguage] || ''
        };

        exportLanguages
          .filter(language => primaryLanguage !== language)
          .forEach(language => {
            translationObject[language] = node?.label[language] || '';
          });

        return { ...translationObject, [TRANSLATION_REFERENCES_COLUMN]: keyValue };
      })
    );

    contentToBeExported = [...contentToBeExported, ...translationsArray];

  });

  return PapaParse.unparse(contentToBeExported, {
    header: true,
    skipEmptyLines: true
  });
};

/**
 * Converts provided data to PO format for gettext compatibility.
 * This function processes the input data and prepares it in a format suitable for PO file generation.
 * @param {Object} data - The source data to be converted.
 * @param {String} primaryLanguage - The primary language code for the translation entries.
 * @param {String} [timezone='UTC'] - The timezone used for the file creation date.
 * @returns {String} The PO formatted data ready to be compiled into a .po file.
 */
const convertDataToPo = ({ data, primaryLanguage, timezone = 'UTC' }) => {

  const fileCreationDate = moment
    .tz(Date.now(), timezone)
    ?.format(DEFAULT_DATETIME_TIMEZONE_FORMAT);
  const poData = {
    charset: UTF_8_CHARSET,
    headers: {
      'POT-Creation-Date': fileCreationDate,
      'Content-Type': FILE_MIME_TYPES.pot,
      Language: primaryLanguage,
      'Plural-Forms': 'nplurals=2; plural=(n != 1);'
    },
    translations: {
      '': {}
    }
  };

  Object.entries(data).forEach(([key, value]) => {
    value?.forEach((entity) => {
      entity?.content?.nodes?.forEach((node) => {
        if (
          node?.label
              && Object.values(TRANSLATION_EXPORT_ALLOWED_NODE_TYPES).includes(
                node?.type
              )
        ) {
          const labelText = node.label[primaryLanguage];
          const entityId = entity.id;
          const nodeId = node.id;
          const keyValue = `${key}:${entityId}/${nodeId}`;

          if (labelText) {
            // Check if label already exists and append reference if so
            if (!poData.translations[''][labelText]) {
              poData.translations[''][labelText] = {
                msgid: labelText,
                msgstr: [''], // Initialize empty translation string
                comments: {
                  reference: keyValue
                }
              };
            } else {
              // Append additional reference if the label is reused
              poData.translations[''][
                labelText
              ].comments.reference += `,${keyValue}`;
            }
          }
        }
      });
    });
  });

  return po.compile(poData);
};
